import { type FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, Nav } from 'react-bootstrap';

import { type RootState } from '@editor2/reducers/root';
import Header from '@editor2/components/Header';
import {
	checkUnsavedTocChanges,
	checkUnsavedConfigChanges
} from '@editor2/services/unsavedChangesService';
import UnsavedChangesTooltip from '@editor2/components/UnsavedChangesTooltip';

import ChapterEditorForm from './components/ChapterEditorForm';
import { chapterPage } from './styles';
import TableOfContents from './components/TableOfContents';
import ChapterSettings from './components/ChapterSettings';
import Repaginator from './components/Repaginator';
import { updateActiveTab } from '@editor2/reducers/editor/editorSlice';
import { useConnectSocket } from '@editor2/hooks/useConnectSocket';
import { useTabTitle } from '@editor2/hooks/useTabTitle';
import { sanitizeCourseId } from '@editor2/services/helpersService';
import Navigation from '@editor2/components/Header/Navigation';
import { useSettingsMode } from '@editor2/hooks/useSettingsMode';
import { checkTypeIsSet } from '@editor2/services/configService';
import SideOptionsPanel from '../App/SideOptionsPanel';

interface Props {
	match: {
		params: {
			courseId: string;
			chapterId: string;
		};
	};
}

const ChapterPage: FC<Props> = (props) => {
	const { match } = props;
	const { courseId: courseIdParam, chapterId } = match.params;
	const courseId = sanitizeCourseId(courseIdParam);

	const dispatch = useDispatch();

	const { course, chapters, changes, settingsMode, activeTab, config } = useSelector(
		(state: RootState) => state.courses[courseId]
	);
	const chapter = chapters && chapters.entities[chapterId];

	const [key, setKey] = useState(activeTab.chapters || 'toc');

	useConnectSocket({ roomName: `chapter.${chapterId}`, courseId });
	useTabTitle({ title: `${course.name}: ${chapter.name}` });

	const onTabChange = (key: string) => {
		setKey(key);
		dispatch(updateActiveTab({ courseId, key: 'chapters', activeTab: key }));
	};

	useEffect(() => {
		if (
			checkUnsavedConfigChanges(changes) ||
			!checkTypeIsSet(config.chapter_types, chapter.chapter_type)
		)
			setKey('settings');
		if (key === 'settings' && checkUnsavedTocChanges(changes) && !settingsMode) setKey('toc');
	}, [changes]);

	useSettingsMode({ settingsMode, setKey });

	return (
		chapter && (
			<div className={chapterPage}>
				<Header
					course={{ id: courseId, name: course.name, number: course.number }}
					chapter={chapter}
				/>
				<SideOptionsPanel courseId={courseId} />
				<Navigation courseId={courseId} chapter={chapter} />

				<Tab.Container id="" activeKey={key} onSelect={(k) => onTabChange(k)} unmountOnExit={true}>
					<Nav variant="tabs" className="tocTabs">
						<UnsavedChangesTooltip condition={checkUnsavedConfigChanges(changes) || settingsMode}>
							<Nav.Item>
								<Nav.Link
									eventKey="chapter"
									disabled={
										checkUnsavedConfigChanges(changes) ||
										!checkTypeIsSet(config.chapter_types, chapter.chapter_type) ||
										settingsMode
									}>
									Properties
								</Nav.Link>
							</Nav.Item>
						</UnsavedChangesTooltip>

						<UnsavedChangesTooltip condition={checkUnsavedConfigChanges(changes) || settingsMode}>
							<Nav.Item>
								<Nav.Link
									eventKey="toc"
									disabled={
										checkUnsavedConfigChanges(changes) ||
										!checkTypeIsSet(config.chapter_types, chapter.chapter_type) ||
										settingsMode
									}>
									Contents
								</Nav.Link>
							</Nav.Item>
						</UnsavedChangesTooltip>

						<UnsavedChangesTooltip condition={checkUnsavedConfigChanges(changes) || settingsMode}>
							<Nav.Item>
								<Nav.Link
									eventKey="reorder"
									disabled={
										checkUnsavedConfigChanges(changes) ||
										!checkTypeIsSet(config.chapter_types, chapter.chapter_type) ||
										settingsMode
									}>
									Reorder Elements
								</Nav.Link>
							</Nav.Item>
						</UnsavedChangesTooltip>

						<UnsavedChangesTooltip condition={checkUnsavedTocChanges(changes) && !settingsMode}>
							<Nav.Item>
								<Nav.Link
									eventKey="settings"
									disabled={checkUnsavedTocChanges(changes) && !settingsMode}>
									Settings
								</Nav.Link>
							</Nav.Item>
						</UnsavedChangesTooltip>
					</Nav>

					<Tab.Content>
						<Tab.Pane eventKey="chapter">
							<ChapterEditorForm chapter={chapter} courseId={courseId} key={chapter.family_id} />
						</Tab.Pane>

						<Tab.Pane eventKey="toc">
							<TableOfContents courseId={courseId} chapterId={chapterId} />
						</Tab.Pane>

						<Tab.Pane eventKey="reorder">
							<Repaginator courseId={courseId} chapterId={chapterId} key={chapter.family_id} />
						</Tab.Pane>

						<Tab.Pane eventKey="settings">
							<ChapterSettings courseId={courseId} chapter={chapter} />
						</Tab.Pane>
					</Tab.Content>
				</Tab.Container>
			</div>
		)
	);
};

export default ChapterPage;
